import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import GlossaryText from "../components/GlossaryText";
import { useAiConfirm } from "../components/AiConfirmDialog";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { clearAppCacheAndReload } from "../lib/appRefresh";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, SeverityDot, AccessBadge } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StandardRsaw } from "../components/StandardRsaw";
import ApprovalWorkflow from "../components/ApprovalWorkflow";
import EvidenceDropzone from "../components/EvidenceDropzone";
import EvidenceDetails from "../components/EvidenceDetails";
import { Sparkle, FloppyDisk, Notebook, Stack, ArrowsClockwise } from "@phosphor-icons/react";

const SECTIONS = [
  ["how_met", "How the requirement is met"],
  ["process_performed", "What process is performed"],
  ["who_performs", "Who performs it"],
  ["how_often", "How often it occurs"],
  ["evidence_summary", "What evidence supports it"],
  ["exceptions", "Exceptions / manual notes"],
];
const blank = Object.fromEntries(SECTIONS.map(([k]) => [k, ""]));

const FLOW = {
  draft: [["review", "Submit for Review"]],
  review: [["approved", "Approve"], ["changes_requested", "Request Changes"]],
  changes_requested: [["review", "Resubmit"]],
  approved: [],
};

export default function Rsaw() {
  const { confirmAi, aiConfirmDialog } = useAiConfirm();
  const [params, setParams] = useSearchParams();
  const { canEdit, isRole, aiEnabled , aiConfidentialBlocked } = useAuth();
  const aiOff = !aiEnabled || aiConfidentialBlocked;
  const aiOffTitle = !aiEnabled ? "AI features are turned off for this entity" : aiConfidentialBlocked ? "Narrative generation works with your program/evidence and is blocked while AI is set to Public-information only" : undefined;
  const { isSubHidden } = useModuleVisibility();
  const hideStd = isSubHidden("rsaw:standard");
  const hideSingle = isSubHidden("rsaw:single");
  const [mode, setMode] = useState(params.get("req") ? "single" : "standard");
  useEffect(() => {
    if (mode === "standard" && hideStd) setMode("single");
    else if (mode === "single" && hideSingle) setMode("standard");
  }, [mode, hideStd, hideSingle]);
  const [reqs, setReqs] = useState([]);
  const [selId, setSelId] = useState(params.get("req") || "");
  const [detail, setDetail] = useState(null);
  const [sections, setSections] = useState(blank);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");

  useEffect(() => { api.get("/requirements").then((r) => { setReqs(r.data); setSelId((cur) => cur || (r.data[0]?.id ?? "")); }); }, []);

  const load = useCallback(() => {
    if (!selId) return;
    api.get(`/requirements/${selId}`).then((r) => {
      setDetail(r.data);
      setSections(r.data.narrative?.sections || blank);
    });
  }, [selId]);
  useEffect(() => { load(); if (selId) setParams({ req: selId }); }, [load, selId, setParams]);

  const editRequirement = useCallback((rid) => { setSelId(rid); setMode("single"); }, []);

  const generate = async () => {
    if (!(await confirmAi())) return;
    setGenerating(true);
    setGenError("");
    try {
      const data = await runAiJob(`/narratives/${selId}/generate?confirm_confidential=1`);
      setSections(data.sections);
      toast.success("AI narrative generated — review & save");
    } catch (e) {
      setGenError(formatApiError(e.response?.data?.detail));
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setGenerating(false); }
  };

  const save = async () => {
    try {
      await api.put(`/narratives/${selId}`, { sections });
      load(); toast.success("Narrative saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    try { await api.post(`/narratives/${selId}/status`, { status }); load(); toast.success(`Status: ${status}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const req = reqs.find((r) => r.id === selId);
  const nar = detail?.narrative;
  const access = detail?.requirement?.access || {};
  const gated = !!access.gated; // category2_starter lockout (Phase 2 — visual read-only)
  const writable = canEdit && !gated;

  return (
    <div className="flex flex-col h-screen">
      {aiConfirmDialog}
      <div className="border-b border-border px-8 pt-3 bg-card flex items-center gap-1">
        {!hideStd && <button data-testid="rsaw-mode-standard" onClick={() => setMode("standard")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 ${mode === "standard" ? "border-black text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Stack size={16} /> Standard RSAW (turnkey)
        </button>}
        {!hideSingle && <button data-testid="rsaw-mode-single" onClick={() => setMode("single")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 ${mode === "single" ? "border-black text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          <Notebook size={16} /> Single Requirement
        </button>}
      </div>

      {mode === "standard" ? (
        <div className="flex-1 overflow-y-auto"><StandardRsaw onEdit={editRequirement} /></div>
      ) : (
      <>
      <div className="border-b border-border px-8 py-4 bg-card flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className="overline text-muted-foreground mb-1">RSAW Workspace</div>
          <Select value={selId} onValueChange={setSelId}>
            <SelectTrigger data-testid="rsaw-req-select" className="rounded-none max-w-xl"><SelectValue placeholder="Select a requirement" /></SelectTrigger>
            <SelectContent>{reqs.map((r) => <SelectItem key={r.id} value={r.id}>{r.standard_id} {r.req_number} — {r.text.slice(0, 60)}…</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {nar && <StatusBadge status={nar.status} data-testid="rsaw-status" />}
        <AccessBadge access={access} data-testid="rsaw-single-access" />
      </div>

      {gated && (
        <div data-testid="rsaw-locked-banner" className="border-b border-amber-300 bg-amber-50 px-8 py-2.5 text-sm text-amber-800">
          {access.state === "historical"
            ? "This requirement is retired and read-only for backward audits."
            : "This Requirement is outside your current Registration scope. Upgrade your plan to unlock the full active NERC Compliance suite."}
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
        {/* Left: narrative editor */}
        <div className="overflow-y-auto border-r border-border p-6 space-y-4" data-testid="rsaw-narrative-editor">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-lg">Narrative Draft</h3>
            {writable && (
              <div className="flex gap-2">
                <Button data-testid="generate-narrative-btn" onClick={generate} disabled={generating || !selId || nar?.locked || aiOff} title={aiOffTitle} variant="outline" className="rounded-none">
                  <Sparkle size={16} weight="fill" className="mr-1" />{generating ? "Generating…" : "AI Draft"}
                </Button>
                <Button data-testid="save-narrative-btn" onClick={save} disabled={!selId || nar?.locked} className="rounded-none bg-primary hover:bg-primary/90"><FloppyDisk size={16} className="mr-1" />Save</Button>
              </div>
            )}
          </div>
          {genError && (
            <div data-testid="narrative-gen-error" className="border border-red-300 bg-red-50 p-3 text-sm text-red-800 space-y-2">
              <div>Generation failed: {genError}</div>
              <div className="text-xs text-red-700">If this keeps happening, the app may be running a cached version. Refresh it and try again:</div>
              <button data-testid="narrative-refresh-app-btn" onClick={clearAppCacheAndReload}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-900 underline">
                <ArrowsClockwise size={14} /> Refresh app &amp; retry
              </button>
            </div>
          )}
          {SECTIONS.map(([key, label]) => (
            <div key={key}>
              <label className="overline text-muted-foreground">{label}</label>
              <Textarea data-testid={`narrative-${key}`} value={sections[key] || ""} disabled={!writable || nar?.locked}
                onChange={(e) => setSections({ ...sections, [key]: e.target.value })}
                className="rounded-none mt-1 min-h-[80px] text-sm" placeholder={`Describe: ${label.toLowerCase()}…`} />
            </div>
          ))}
          {writable && nar && FLOW[nar.status]?.length > 0 && (
            <div className="flex gap-2 pt-2 border-t border-border">
              {FLOW[nar.status].map(([s, l]) => <Button key={s} data-testid={`narrative-status-${s}`} onClick={() => changeStatus(s)} className="rounded-none bg-primary hover:bg-primary/90">{l}</Button>)}
            </div>
          )}
        </div>

        {/* Right: requirement context + evidence checklist + gaps */}
        <div className="overflow-y-auto p-6 space-y-5 bg-secondary/20">
          {req && (
            <div className="border border-border bg-card p-4">
              <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{req.standard_id} {req.req_number}</span>
              <p className="text-sm mt-3"><GlossaryText text={req.text} /></p>
              {req.measure && (
                <div className="mt-3 border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-3 py-2" data-testid="rsaw-req-measure">
                  <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure — evidence auditors look for</span>
                  <p className="text-sm mt-0.5 whitespace-pre-line"><GlossaryText text={req.measure} /></p>
                </div>
              )}
              {(detail?.requirement?.sub_requirements || []).filter((sp) => sp.measure).map((sp) => (
                <div key={sp.label} className="mt-2 border-l-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-950/30 pl-3 py-1.5" data-testid={`rsaw-part-measure-${sp.label}`}>
                  <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure · Part {sp.label}</span>
                  <p className="text-xs mt-0.5 whitespace-pre-line"><GlossaryText text={sp.measure} /></p>
                </div>
              ))}
            </div>
          )}
          {detail && (
            <>
              <div className="border border-border bg-card">
                <div className="border-b border-border px-4 py-2.5 font-display font-bold flex justify-between">Linked Controls <span className="font-mono text-sm text-muted-foreground">{detail.controls.length}</span></div>
                <div className="p-4 space-y-1 text-sm">
                  {detail.controls.map((c) => <div key={c.id}>• {c.name} <span className="text-xs text-muted-foreground">({c.frequency})</span></div>)}
                  {detail.controls.length === 0 && <p className="text-sm text-muted-foreground">None mapped.</p>}
                </div>
              </div>
              <div className="border border-border bg-card">
                <div className="border-b border-border px-4 py-2.5 font-display font-bold flex justify-between">Evidence Checklist <span className="font-mono text-sm text-muted-foreground">{detail.evidence.length}</span></div>
                <div className="p-4 space-y-3 text-sm">
                  {detail.evidence.map((e) => (
                    <EvidenceDetails key={e.id} evidence={e} requirementId={selId}
                      onSaved={load} readOnly={!writable || nar?.locked} />
                  ))}
                  {detail.evidence.length === 0 && <p className="text-sm text-red-600">No evidence attached.</p>}
                  <div className="pt-2 border-t border-border">
                    <EvidenceDropzone
                      requirementId={selId}
                      parts={detail?.requirement?.sub_requirements || []}
                      onUploaded={load}
                      disabled={!writable || nar?.locked}
                      testid="rsaw-evidence-dropzone"
                    />
                  </div>
                </div>
              </div>
              <div className="border border-red-300 bg-card" data-testid="rsaw-gaps">
                <div className="border-b border-red-300 bg-red-50 px-4 py-2.5 font-display font-bold text-red-700">Missing Evidence ({detail.gaps.length})</div>
                <div className="p-4 space-y-2">
                  {detail.gaps.length === 0 && <p className="text-sm text-green-700">Fully supported.</p>}
                  {detail.gaps.map((g, i) => <div key={`${g.severity}-${i}`} className="flex gap-2 items-start"><SeverityDot severity={g.severity} /><p className="text-sm">{g.message}</p></div>)}
                </div>
              </div>
              {nar ? (
                <ApprovalWorkflow
                  doc={nar}
                  entityId={selId}
                  basePath="/narratives"
                  manageRoles={["admin", "analyst", "reviewer"]}
                  entityLabel="narrative"
                  bumpsVersion={false}
                  onChange={() => load()}
                />
              ) : (
                <div className="border border-border bg-card px-4 py-3 text-sm text-muted-foreground" data-testid="rsaw-approval-hint">
                  Save the narrative first to route it for sign-off.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
